import type { ColumnDef } from "@tanstack/react-table";

import type {
  MetricCol,
  MetricKey,
  MetricType,
  PerfRow,
  ViewTab,
} from "../types";

import { InfoIcon } from "../../Icons";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import { fmtInt, fmtPct, fmtUsd, hasMetric, metricVal } from "../helpers";
import { SourceCell } from "./SourceCell";

const EmptyCell = () => (
  <Typo element="span" type="bodyMD" textColor="text-text-disabled">
    —
  </Typo>
);

const MetricValue = ({
  row,
  col,
  usdLabel,
}: {
  row: PerfRow;
  col: MetricCol;
  usdLabel: string;
}) => {
  if (!hasMetric(row, col.key)) return <EmptyCell />;
  const v = metricVal(row, col.key);

  if (col.type === "usd") {
    return (
      <Typo
        element="span"
        type="bodyMD"
        textColor="text-text-primary"
        className="tabular-nums"
      >
        {fmtUsd(v)}
        <Typo
          element="span"
          type="bodyXS"
          textColor="text-text-secondary"
          className="ml-1"
        >
          {usdLabel}
        </Typo>
      </Typo>
    );
  }
  if (col.type === "pct") {
    return (
      <Typo
        element="span"
        type="labelSM"
        textColor="text-text-primary"
        className="tabular-nums"
      >
        {fmtPct(v)}
      </Typo>
    );
  }
  return (
    <Typo
      element="span"
      type="bodyMD"
      textColor="text-text-primary"
      className="tabular-nums"
    >
      {fmtInt(v)}
    </Typo>
  );
};

const MetricHeader = ({
  col,
  conversionTips,
}: {
  col: MetricCol;
  conversionTips: Partial<Record<MetricKey, string>>;
}) => (
  <span className="inline-flex items-center gap-1">
    <Typo element="span" type="bodyXS" textColor="text-text-secondary">
      {col.label}
    </Typo>
    {col.type === "pct" && conversionTips[col.key] && (
      <TooltipWrapper
        color="blue"
        tooltipPlacement="top"
        tooltipTextAlign="text-center"
        text={conversionTips[col.key] as string}
        positionStrategy="fixed"
      >
        <InfoIcon
          width={14}
          height={14}
          className="[&_path]:stroke-icon-tertiary"
          aria-hidden="true"
          focusable="false"
        />
      </TooltipWrapper>
    )}
  </span>
);

const METRIC_COLUMN_MIN_WIDTH: Record<MetricType, number> = {
  int: 120,
  pct: 96,
  usd: 150,
};

/**
 * Builds the TanStack column set for the current tab. `orderedCols` is the
 * visible metric columns already arranged in display order (funnel column
 * followed by its conversion %). The Source column is always first (pinned).
 */
export const buildColumns = (
  tab: ViewTab,
  orderedCols: MetricCol[],
  sourceColumnLabels: Record<ViewTab, string>,
  conversionTips: Partial<Record<MetricKey, string>>,
  usdLabel: string,
): ColumnDef<PerfRow, unknown>[] => {
  // Name-only dimensions need far less room than the source tab, whose cell
  // stacks a landing page and language under the title.
  const sourceWidth =
    tab === "country" || tab === "rebate" || tab === "campaign" ? 240 : 400;

  const sourceColumn: ColumnDef<PerfRow, unknown> = {
    id: "source",
    accessorFn: (row) => row.sortName,
    header: () => (
      <Typo element="span" type="bodyXS" textColor="text-text-secondary">
        {sourceColumnLabels[tab]}
      </Typo>
    ),
    cell: ({ row }) => <SourceCell row={row.original} />,
    enableSorting: true,
    sortingFn: "alphanumeric",
    // Without this, TanStack infers first-click direction from the first
    // row's live value type (string ⇒ ascending), which put this column out
    // of step with every metric column (number-accessed, descending-first by
    // the same inference) — PART-4149.
    sortDescFirst: true,
    size: sourceWidth,
    // Below this, the meta row (landing-page name + language, each with
    // their own icon) has too little room to stay legible even truncated —
    // 220px keeps the title mostly readable, language fully visible, and
    // the landing-page name showing a recognizable truncated fragment
    // rather than 1-2 characters.
    minSize: 220,
  };

  const metricColumns: ColumnDef<PerfRow, unknown>[] = orderedCols.map(
    (col) => ({
      id: col.key,
      // Rows the metric doesn't apply to (e.g. Impressions on referral-link
      // rows) return undefined rather than a numeric sentinel so sortUndefined
      // keeps them out of the numeric ordering entirely — a -1 sentinel sorted
      // as a real value and interleaved the "—" rows with genuine data (PART-4190).
      accessorFn: (row) =>
        hasMetric(row, col.key) ? metricVal(row, col.key) : undefined,
      header: () => <MetricHeader col={col} conversionTips={conversionTips} />,
      cell: ({ row }) => (
        <MetricValue row={row.original} col={col} usdLabel={usdLabel} />
      ),
      enableSorting: true,
      sortingFn: "basic",
      // Empty ("—") rows always sort last, in both directions — consistent with
      // the other numeric columns, which never produce an empty value.
      sortUndefined: "last",
      size: METRIC_COLUMN_MIN_WIDTH[col.type],
    }),
  );

  return [sourceColumn, ...metricColumns];
};
